import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHead from "../SectionHead";

const INDUSTRIES = [
    {
        name: "Electrical & Power",
        img: "https://images.unsplash.com/photo-1509390221805-d1c887a72a00?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Transformer Industry",
        img: "https://images.unsplash.com/photo-1509390144018-eeaf65052242?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Industrial Equipment",
        img: "https://images.unsplash.com/photo-1695603414720-2fbd4db0a434?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Engineering Components",
        img: "https://images.unsplash.com/photo-1695603414724-2396274ef5cc?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Electrical Insulation",
        img: "https://images.unsplash.com/photo-1695603414659-4d573962b517?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Industrial Fabrication",
        img: "https://images.unsplash.com/photo-1610891015188-5369212db097?q=80&w=1200&auto=format&fit=crop",
    },
    {
        name: "Specialized Engineering Applications",
        img: "https://images.unsplash.com/photo-1717386255767-52643970d483?q=80&w=1200&auto=format&fit=crop",
    },
];

export const Industries = () => {
    const [active, setActive] = useState(0);

    return (
        <section id="industries" className="py-24 md:py-32 scroll-mt-20 bg-panel/40 border-y border-zinc-900">
            <div className="shell">
                <SectionHead
                    num="10"
                    overline="Industries Served"
                    title="Where our FRP works"
                    desc="Our FRP products and components are suitable for applications across electrical, transformer and industrial engineering environments."
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 border-t border-zinc-800">
                        {INDUSTRIES.map((ind, i) => (
                            <motion.div
                                key={ind.name}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.45, delay: i * 0.05 }}
                                onMouseEnter={() => setActive(i)}
                                data-testid={`industry-${i + 1}`}
                                className={`group flex items-center justify-between gap-4 py-5 md:py-6 border-b border-zinc-800 cursor-default px-2 md:px-4 transition-colors duration-300 ${
                                    active === i ? "bg-panel" : ""
                                }`}
                            >
                                <div className="flex items-center gap-5 md:gap-8 min-w-0">
                                    <span
                                        className={`font-mono text-xs w-8 shrink-0 transition-colors ${
                                            active === i ? "text-safety" : "text-zinc-600"
                                        }`}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span
                                        className={`font-display uppercase font-medium tracking-wide text-lg md:text-3xl transition-colors duration-300 ${
                                            active === i ? "text-safety" : "text-zinc-300"
                                        }`}
                                    >
                                        {ind.name}
                                    </span>
                                </div>
                                <ArrowUpRight
                                    size={20}
                                    className={`shrink-0 transition-all duration-300 ${
                                        active === i
                                            ? "text-safety translate-x-0 opacity-100"
                                            : "text-zinc-700 -translate-x-2 opacity-40"
                                    }`}
                                />
                            </motion.div>
                        ))}
                    </div>

                    <div className="hidden lg:block lg:col-span-5">
                        <div
                            className="relative border border-zinc-800 h-[520px] sticky top-24 overflow-hidden"
                            data-testid="industry-preview-panel"
                        >
                            <AnimatePresence mode="popLayout">
                                <motion.img
                                    key={active}
                                    src={INDUSTRIES[active].img}
                                    alt={INDUSTRIES[active].name}
                                    initial={{ opacity: 0, scale: 1.06 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </AnimatePresence>
                            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/90 via-transparent to-[#09090b]/30" />
                            <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety">
                                        Application Field
                                    </div>
                                    <div className="font-display uppercase font-medium text-xl text-zinc-50 mt-1">
                                        {INDUSTRIES[active].name}
                                    </div>
                                </div>
                                <span className="font-display text-5xl font-semibold text-zinc-800">
                                    {String(active + 1).padStart(2, "0")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Industries;